import { apiService } from '../services/apiService';

/**
 * PDF生成工具函數集合
 * 使用示例和常用場景
 */

// 生成訂單概覽PDF
export const generateInboundVoucherPDF = async (orderNumber: string) => {
  const { PDFService } = await import('../services/pdfService');
  await PDFService.generatePDFFromAPI(
    () => apiService.getInboundVoucherHTML(orderNumber),
    {
      filename: `訂單概覽-${orderNumber}.pdf`,
      loadingMessage: '正在生成訂單概覽PDF...',
      successMessage: '訂單概覽PDF已在新窗口中打開',
      errorMessage: '訂單概覽PDF生成失敗，請稍後重試',
      quality: 2
    }
  );
};

// 獲取入庫憑證PDF (使用PDFKit - 支持多頁重複表頭)
export const generateInboundBeePDF = async (orderNumber: string) => {
    const { message } = await import('antd');
    try {
        message.loading({ content: '正在生成入庫憑證PDF...', key: 'inbound-pdf-loading' });

        const blob = await apiService.downloadInboundBeePDFKit(orderNumber);

        // Open PDF in new window
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');

        message.success({ content: '入庫憑證PDF已在新窗口中打開', key: 'inbound-pdf-loading' });
    } catch (error) {
        console.error('Error generating inbound voucher PDF:', error);
        message.error({ content: '入庫憑證PDF生成失敗，請稍後重試', key: 'inbound-pdf-loading' });
    }
};

// 下載入庫憑證Excel
export const generateInboundBeeExcel = async (orderNumber: string) => {
  try {
    const { message } = await import('antd');
    message.loading({ content: '正在生成入庫憑證Excel...', key: 'excel-loading' });

    const blob = await apiService.getInboundBeeExcel(orderNumber);

    // Create download link
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `入庫憑證-${orderNumber}.xlsx`;
    document.body.appendChild(a);
    a.click();

    // Cleanup
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);

    message.success({ content: '入庫憑證Excel已下載', key: 'excel-loading' });
  } catch (error) {
    const { message } = await import('antd');
    message.error({ content: '入庫憑證Excel生成失敗，請稍後重試', key: 'excel-loading' });
    throw error;
  }
};


//  生成揀貨單PDF
export const generatePickingOrderPDF = async (batchNumber: string) => {
  const { PDFService } = await import('../services/pdfService');
  await PDFService.generatePDFFromAPI(
    () => apiService.getPickingOrderHTML(batchNumber),
    {
      filename: `picking-order-${batchNumber}.pdf`,
      loadingMessage: '正在生成揀貨單PDF...',
      successMessage: '揀貨單PDF已在新窗口中打開',
      errorMessage: '揀貨單PDF生成失敗，請稍後重試',
      quality: 1,
      paperSize: 'a4',
      orientation: 'portrait'
    }
  );
};

// 生成揀貨單PDF - 使用後端PDFKit生成，避免瀏覽器差異導致的排版問題
export const downloadPickingOrderPDFDirect = async (batchNumber: string) => {
    const { message } = await import('antd');
    try {
        message.loading({ content: '正在生成揀貨單PDF...', key: 'picking-order-loading' });
        const { blob, fileName } = await apiService.downloadPickingOrderPDF({
            batchNumber
        });
        // Open PDF in new window
        const blobUrl = URL.createObjectURL(blob);
        const newWindow = window.open(blobUrl, '_blank');
        if (newWindow) {
            message.success({ content: '揀貨單PDF已在新窗口中打開', key: 'picking-order-loading' });
            // Clean up blob URL after a delay
            setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
        } else {
            // Fallback: download the file
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(blobUrl);
            message.success({ content: '揀貨單PDF已下載', key: 'picking-order-loading' });
        }
    } catch (error) {
        console.error('Error generating picking order PDF:', error);
        message.error({ content: '揀貨單PDF生成失敗，請稍後重試', key: 'picking-order-loading' });
    }
};

// 生成運單PDF【HKtvmall】
export const getHktvMallWaybillHtml = async (orderNumber: string, waybillNumber: string) => {
    const { PDFService } = await import('../services/pdfService');
    await PDFService.generatePDFFromAPI(
        () => apiService.getHktvMallWaybillHtml(orderNumber, waybillNumber), // 假設有這個API
        {
            filename: `waybill-${waybillNumber}.pdf`,
            loadingMessage: '正在生成運單PDF...',
            successMessage: '運單PDF已在新窗口中打開',
            errorMessage: '運單PDF生成失敗，請稍後重試',
            quality: 2,
            paperSize: 'a5',
            orientation: 'landscape'
        }
    );
};

// 獲取運單PDF【不是HKtvmall】- 使用後端PDFKit生成，支持表頭重複
export const getWaybillHtml = async (orderNumber: string, waybillNumber: string) => {
    const { message } = await import('antd');
    try {
        message.loading({ content: '正在生成運單PDF...', key: 'waybill-loading' });
        const { blob, fileName } = await apiService.downloadWaybillPDF({
            waybillNumber,
            orderNumber
        });
        // Open PDF in new window
        const blobUrl = URL.createObjectURL(blob);
        const newWindow = window.open(blobUrl, '_blank');
        if (newWindow) {
            message.success({ content: '運單PDF已在新窗口中打開', key: 'waybill-loading' });
            // Clean up blob URL after a delay
            setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
        } else {
            // Fallback: download the file
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(blobUrl);
            message.success({ content: '運單PDF已下載', key: 'waybill-loading' });
        }
    } catch (error) {
        console.error('Error generating waybill PDF:', error);
        message.error({ content: '運單PDF生成失敗，請稍後重試', key: 'waybill-loading' });
    }
};

// 【新增】自動打印 HKTVmall 運單（同時生成PDF）
export const printHktvMallWaybillAuto = async (orderNumber: string, waybillNumber: string) => {
    const { PDFService } = await import('../services/pdfService');
    await PDFService.printAndGeneratePDFFromAPI(
        () => apiService.getHktvMallWaybillHtml(orderNumber, waybillNumber),
        {
            filename: `waybill-${waybillNumber}.pdf`,
            printTitle: `運單 ${waybillNumber}`,
            loadingMessage: '正在獲取運單資料...',
            successMessage: '打印對話框已打開，PDF已開始下載',
            errorMessage: '運單打印失敗，請稍後重試',
            quality: 2,
            paperSize: 'a5',
            orientation: 'landscape',
            onPrintComplete: () => {
                console.log(`運單 ${waybillNumber} 打印完成`);
            }
        }
    );
};

// 【新增】自動打印普通運單（使用後端PDFKit生成）
export const printWaybillAuto = async (orderNumber: string, waybillNumber: string) => {
    const { message } = await import('antd');
    try {
        message.loading({ content: '正在獲取運單資料...', key: 'waybill-print' });
        const { blob } = await apiService.downloadWaybillPDF({
            waybillNumber,
            orderNumber
        });
        // Open PDF in new window for printing
        const blobUrl = URL.createObjectURL(blob);
        const printWindow = window.open(blobUrl, '_blank');
        if (printWindow) {
            message.success({ content: '打印對話框已打開', key: 'waybill-print' });
            console.log(`運單 ${waybillNumber} 打印完成`);
            // Clean up blob URL after a delay
            setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
        } else {
            message.warning({ content: '無法打開打印窗口，請檢查瀏覽器設置', key: 'waybill-print' });
            URL.revokeObjectURL(blobUrl);
        }
    } catch (error) {
        console.error('Error printing waybill:', error);
        message.error({ content: '運單打印失敗，請稍後重試', key: 'waybill-print' });
    }
};

// 【新增】靜默打印 HKTVmall 運單（不打開新視窗）
export const printHktvMallWaybillSilently = async (orderNumber: string, waybillNumber: string) => {
    const { PDFService } = await import('../services/pdfService');
    await PDFService.printPDFSilentlyFromAPI(
        () => apiService.getHktvMallWaybillHtml(orderNumber, waybillNumber),
        {
            filename: `waybill-${waybillNumber}.pdf`,
            printTitle: `運單 ${waybillNumber}`,
            loadingMessage: '正在生成運單...',
            successMessage: `運單 ${waybillNumber} 已發送至打印機`,
            errorMessage: '運單打印失敗，請稍後重試',
            quality: 2, // Balanced quality for faster generation
            paperSize: 'a5',
            orientation: 'landscape',
            onPrintComplete: () => {
                console.log(`運單 ${waybillNumber} 靜默打印完成`);
            }
        }
    );
};

// 【新增】靜默打印普通運單（使用後端PDFKit生成）
export const printWaybillSilently = async (orderNumber: string, waybillNumber: string) => {
    const { message } = await import('antd');
    try {
        message.loading({ content: '正在生成運單...', key: 'waybill-silent' });
        const { blob } = await apiService.downloadWaybillPDF({
            waybillNumber,
            orderNumber
        });
        // Create hidden iframe for silent printing
        const blobUrl = URL.createObjectURL(blob);
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.src = blobUrl;
        document.body.appendChild(iframe);

        iframe.onload = () => {
            try {
                iframe.contentWindow?.print();
                message.success({ content: `運單 ${waybillNumber} 已發送至打印機`, key: 'waybill-silent' });
                console.log(`運單 ${waybillNumber} 靜默打印完成`);
            } catch (e) {
                console.error('Silent print failed:', e);
                // Fallback: open in new window
                window.open(blobUrl, '_blank');
            }
            // Clean up after delay
            setTimeout(() => {
                document.body.removeChild(iframe);
                URL.revokeObjectURL(blobUrl);
            }, 5000);
        };
    } catch (error) {
        console.error('Error silent printing waybill:', error);
        message.error({ content: '運單打印失敗，請稍後重試', key: 'waybill-silent' });
    }
};

// 【新增】打印 FedEx 運單標籤（4x6熱敏標籤格式）
// 在新視窗中打開 PDF 並自動觸發打印對話框
export const printFedExLabelSilently = async (outboundOrderId: string, trackingNumber?: string) => {
    try {
        console.log(`[FedEx] 開始打印標籤: ${outboundOrderId}`);

        // Get the PDF URL
        const token = localStorage.getItem('authToken') || '';
        const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3002/api/v1';
        const pdfUrl = `${API_BASE_URL}/fedex/outbound/${encodeURIComponent(outboundOrderId)}/label-html`;

        // Fetch the PDF with auth header and convert to blob URL
        const response = await fetch(pdfUrl, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            throw new Error('Failed to fetch FedEx label PDF');
        }

        const pdfBlob = await response.blob();
        const blobUrl = URL.createObjectURL(pdfBlob);

        // Open in new tab
        const newWindow = window.open(blobUrl, '_blank');

        if (newWindow) {
            console.log(`[FedEx] FedEx 標籤 ${trackingNumber || outboundOrderId} 已在新視窗中打開`);

            // Wait for the PDF to load, then trigger print
            setTimeout(() => {
                try {
                    if (!newWindow.closed) {
                        newWindow.focus();
                        newWindow.print();
                        console.log(`[FedEx] FedEx 標籤 ${trackingNumber || outboundOrderId} 打印對話框已觸發`);
                    }
                } catch (printError) {
                    console.log('[FedEx] 無法自動觸發打印，用戶可手動打印:', printError);
                }
            }, 1000);

            // Cleanup URL after window closes or after timeout
            const checkClosed = setInterval(() => {
                if (newWindow.closed) {
                    clearInterval(checkClosed);
                    URL.revokeObjectURL(blobUrl);
                    console.log(`[FedEx] 標籤視窗已關閉，清理資源`);
                }
            }, 1000);

            // Cleanup after 10 minutes max
            setTimeout(() => {
                clearInterval(checkClosed);
                URL.revokeObjectURL(blobUrl);
            }, 600000);
        } else {
            // Popup blocked - download instead
            console.warn('[FedEx] 彈窗被阻止，改為下載PDF');
            const downloadLink = document.createElement('a');
            downloadLink.href = blobUrl;
            downloadLink.download = `fedex-label-${trackingNumber || outboundOrderId}.pdf`;
            downloadLink.click();
            setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
        }

    } catch (error) {
        console.error('[FedEx] 打印 FedEx 標籤失敗:', error);
        throw error;
    }
};

// 【新增】批量打印運單（訂單變更為 PROCESSING 時自動觸發）
// orderWaybillList: Array<{ orderNumber: string, waybillNumber: string, deliveryProvider?: string, outboundOrderId?: string }>
// orderIds: 訂單 ID 列表（用於批量時合併 PDF）
// Chunk size for batch waybill processing to prevent memory issues
// Increased from 30 to 50 since we're now processing PDFs in parallel
const WAYBILL_BATCH_CHUNK_SIZE = 50;

export interface OrderWaybillInfo {
    orderNumber: string;
    waybillNumber: string;
    deliveryProvider?: string;
    outboundOrderId?: string;
    fedexTrackingNo?: string;
    hasFedExLabel?: boolean;  // True if order has FedEx label PDF stored
    hasSFExpressLabel?: boolean;  // True if order has SF Express waybill PDF stored
    sfExpressWaybillPdf?: string;  // Base64 encoded SF Express waybill PDF
}

export const printBatchWaybillsOnProcessing = async (
    orderWaybillList: Array<OrderWaybillInfo>,
    _orderIds?: string[]
) => {
    if (!orderWaybillList || orderWaybillList.length === 0) {
        console.log('[printBatchWaybillsOnProcessing] 沒有運單需要打印');
        return;
    }

    console.log(`[printBatchWaybillsOnProcessing] 開始打印 ${orderWaybillList.length} 張運單`, orderWaybillList);

    // All orders (including FedEx and SF Express) now use system waybill
    // Filter out orders without waybill numbers and log which ones are missing
    const ordersWithoutWaybill = orderWaybillList.filter(item => !item.waybillNumber);
    const validOrders = orderWaybillList.filter(item => item.waybillNumber);

    if (ordersWithoutWaybill.length > 0) {
        console.warn(`[printBatchWaybillsOnProcessing] ${ordersWithoutWaybill.length} 個訂單沒有運單號:`,
            ordersWithoutWaybill.map(o => o.orderNumber));
        // Show warning to user
        const { message } = await import('antd');
        message.warning(`${ordersWithoutWaybill.length} 個訂單沒有運單號，已跳過: ${ordersWithoutWaybill.map(o => o.orderNumber).join(', ')}`, 5);
    }

    console.log(`[printBatchWaybillsOnProcessing] 有效運單數量: ${validOrders.length}`);

    if (validOrders.length === 0) {
        console.log('[printBatchWaybillsOnProcessing] 沒有有效的運單號');
        const { message } = await import('antd');
        message.error('所有訂單都沒有運單號，無法生成運單PDF');
        return;
    }

    // For single order, use the simple approach (open in new tab)
    if (validOrders.length === 1) {
        const { orderNumber, waybillNumber } = validOrders[0];
        // HKTVmall orders start with H25 (e.g., H251217...), not just H
        // HK prefix (e.g., HK11794) is Shopify orders
        const isHktv = orderNumber && /^H\d{2}/.test(orderNumber) && !orderNumber.startsWith('HK');
        console.log(`[printBatchWaybillsOnProcessing] 單張運單，使用簡單模式, isHktv=${isHktv}`);
        if (isHktv) {
            await getHktvMallWaybillHtml(orderNumber, waybillNumber);
        } else {
            await getWaybillHtml(orderNumber, waybillNumber);
        }
        return;
    }

    // For multiple orders, process in chunks to prevent memory issues
    // Then merge all chunks into one final PDF
    console.log(`[printBatchWaybillsOnProcessing] 共 ${validOrders.length} 張運單，使用分批處理後合併為單一PDF模式`);

    const { message } = await import('antd');

    // Split regular orders into chunks
    const chunks: Array<typeof validOrders> = [];
    for (let i = 0; i < validOrders.length; i += WAYBILL_BATCH_CHUNK_SIZE) {
        chunks.push(validOrders.slice(i, i + WAYBILL_BATCH_CHUNK_SIZE));
    }

    const totalChunks = chunks.length;
    if (totalChunks > 0) {
        console.log(`[printBatchWaybillsOnProcessing] 普通運單分成 ${totalChunks} 批處理，每批最多 ${WAYBILL_BATCH_CHUNK_SIZE} 張`);
    }

    const { PDFService } = await import('../services/pdfService');
    const { PDFDocument } = await import('pdf-lib');

    // Collect all chunk PDF bytes for final merge
    const allChunkPdfBytes: Uint8Array[] = [];
    let totalProcessed = 0;

    // Process each chunk
    for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
        const chunk = chunks[chunkIndex];
        const chunkLabel = totalChunks > 1 ? `（第 ${chunkIndex + 1}/${totalChunks} 批）` : '';

        const hide = message.loading(`正在處理 ${chunk.length} 張運單${chunkLabel}，共 ${validOrders.length} 張...`, 0);

        try {
            // Fetch waybill HTML for this chunk (in parallel but limited)
            console.log(`[printBatchWaybillsOnProcessing] 批次 ${chunkIndex + 1}: 正在獲取 ${chunk.length} 張運單HTML...`);
            const htmlPromises = chunk.map(async ({ orderNumber, waybillNumber }) => {
                try {
                    // HKTVmall orders start with H25 (e.g., H251217...), not just H
                    // HK prefix (e.g., HK11794) is Shopify orders
                    const isHktv = orderNumber && /^H\d{2}/.test(orderNumber) && !orderNumber.startsWith('HK');
                    console.log(`[printBatchWaybillsOnProcessing] 獲取運單: orderNumber=${orderNumber}, waybillNumber=${waybillNumber}, isHktv=${isHktv}`);
                    let html: string;
                    if (isHktv) {
                        html = await apiService.getHktvMallWaybillHtml(orderNumber, waybillNumber);
                    } else {
                        html = await apiService.getWaybillHtml(orderNumber, waybillNumber);
                    }
                    console.log(`[printBatchWaybillsOnProcessing] 運單HTML獲取成功: ${orderNumber}, html長度=${html?.length || 0}`);
                    return { orderNumber, waybillNumber, html, isHktv };
                } catch (err) {
                    console.error(`[printBatchWaybillsOnProcessing] 獲取運單HTML失敗: orderNumber=${orderNumber}, waybillNumber=${waybillNumber}`, err);
                    return null;
                }
            });

            const htmlResults = (await Promise.all(htmlPromises)).filter(r => r !== null && r.html);

            if (htmlResults.length === 0) {
                hide();
                console.warn(`[printBatchWaybillsOnProcessing] 批次 ${chunkIndex + 1}: 無法獲取任何運單內容`);
                continue;
            }

            console.log(`[printBatchWaybillsOnProcessing] 批次 ${chunkIndex + 1}: 成功獲取 ${htmlResults.length} 張運單HTML，開始生成PDF...`);

            // Generate individual PDFs for each waybill in this chunk (in parallel)
            const pdfPromises = htmlResults.map(async (result) => {
                if (!result) return null;

                try {
                    const pdfOptions = result.isHktv
                        ? { paperSize: 'a5' as const, orientation: 'landscape' as const, quality: 2 }
                        : { paperSize: 'a4' as const, orientation: 'portrait' as const, quality: 2 };

                    const pdfBytes = await (PDFService as any).pdfBytesFromHTML(result.html, pdfOptions);
                    return pdfBytes;
                } catch (err) {
                    console.error(`[printBatchWaybillsOnProcessing] 處理運單 ${result.waybillNumber} 失敗:`, err);
                    return null;
                }
            });

            const pdfResults = await Promise.all(pdfPromises);
            const successfulPdfs = pdfResults.filter(pdf => pdf !== null) as Uint8Array[];
            allChunkPdfBytes.push(...successfulPdfs);
            totalProcessed += successfulPdfs.length;

            hide();
            console.log(`[printBatchWaybillsOnProcessing] 批次 ${chunkIndex + 1} 完成，已處理 ${totalProcessed}/${validOrders.length} 張`);

            // Small delay between chunks to let browser breathe (reduced from 300ms to 100ms)
            if (chunkIndex < chunks.length - 1) {
                await new Promise(resolve => setTimeout(resolve, 100));
            }

        } catch (err) {
            hide();
            console.error(`[printBatchWaybillsOnProcessing] 批次 ${chunkIndex + 1} 處理失敗:`, err);
            message.warning(`批次 ${chunkIndex + 1} 部分處理失敗，繼續處理其他批次...`);
        }
    }

    // Final merge: combine all PDFs into one
    if (allChunkPdfBytes.length === 0) {
        message.error('無法生成任何運單PDF');
        return;
    }

    const totalWaybillCount = allChunkPdfBytes.length;
    const hideFinalize = message.loading(`正在合併 ${totalWaybillCount} 張運單為單一PDF...`, 0);

    try {
        console.log(`[printBatchWaybillsOnProcessing] 開始最終合併 ${allChunkPdfBytes.length} 個PDF...`);
        const finalPdf = await PDFDocument.create();

        for (const pdfBytes of allChunkPdfBytes) {
            try {
                const pdf = await PDFDocument.load(pdfBytes);
                const pages = await finalPdf.copyPages(pdf, pdf.getPageIndices());
                pages.forEach(page => finalPdf.addPage(page));
            } catch (err) {
                console.error('[printBatchWaybillsOnProcessing] 合併PDF頁面失敗:', err);
            }
        }

        const finalPdfBytes = await finalPdf.save();
        hideFinalize();

        // Open the single merged PDF
        const pdfBlob = new Blob([finalPdfBytes as BlobPart], { type: 'application/pdf' });
        const blobUrl = URL.createObjectURL(pdfBlob);

        const newWindow = window.open(blobUrl, '_blank');

        if (newWindow) {
            console.log(`[printBatchWaybillsOnProcessing] 合併PDF已在新窗口中打開，共 ${allChunkPdfBytes.length} 張運單`);
            message.success(`已生成 ${allChunkPdfBytes.length} 張運單的合併PDF`);

            // Try to trigger print dialog
            setTimeout(() => {
                try {
                    if (!newWindow.closed) {
                        newWindow.print();
                    }
                } catch (err) {
                    console.log('[printBatchWaybillsOnProcessing] 無法自動觸發打印:', err);
                }
            }, 1000);

            // Cleanup URL after window closes
            const checkClosed = setInterval(() => {
                if (newWindow.closed) {
                    clearInterval(checkClosed);
                    URL.revokeObjectURL(blobUrl);
                }
            }, 1000);

            setTimeout(() => {
                clearInterval(checkClosed);
                URL.revokeObjectURL(blobUrl);
            }, 600000);
        } else {
            // Popup blocked - download instead
            const downloadLink = document.createElement('a');
            downloadLink.href = blobUrl;
            downloadLink.download = `waybills_combined_${allChunkPdfBytes.length}.pdf`;
            downloadLink.click();
            setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
            message.info(`彈窗被阻止，已下載 ${allChunkPdfBytes.length} 張運單的合併PDF`);
        }

    } catch (err) {
        hideFinalize();
        console.error('[printBatchWaybillsOnProcessing] 最終合併PDF失敗:', err);
        message.error('合併PDF失敗，請稍後重試');
    }

    console.log('[printBatchWaybillsOnProcessing] 批量打印完成');
};

// 從HTML字符串直接生成PDF
export const generatePDFFromString = async (htmlContent: string, filename: string) => {
  const { PDFService } = await import('../services/pdfService');
  await PDFService.generatePDFFromHTMLString(htmlContent, {
    filename,
    loadingMessage: '正在生成PDF...',
    successMessage: 'PDF已在新窗口中打開',
    errorMessage: 'PDF生成失敗，請稍後重試',
    quality: 2
  });
};

/*// 示例5: 批量生成PDF（依次生成）
export const generateMultiplePDFs = async (orders: Array<{orderNumber: string, type: 'inbound' | 'picking' | 'waybill'}>) => {
  for (const order of orders) {
    try {
      switch (order.type) {
        case 'inbound':
          await generateInboundVoucherPDF(order.orderNumber);
          break;
        case 'picking':
          await generatePickingOrderPDF(order.orderNumber);
          break;
        case 'waybill':
          await generateWaybillPDF(order.orderNumber);
          break;
      }
      // 添加延遲避免瀏覽器阻止過多彈窗
      await new Promise(resolve => setTimeout(resolve, 1000));
    } catch (error) {
      console.error(`生成${order.type} PDF失敗:`, order.orderNumber, error);
    }
  }
};

// 示例6: 自定義配置的PDF生成
export const generateCustomPDF = async (
  apiCall: () => Promise<string>,
  options: {
    filename: string;
    paperSize?: 'a4' | 'a3' | 'letter';
    orientation?: 'portrait' | 'landscape';
    quality?: number;
    downloadDirectly?: boolean;
  }
) => {
  const { PDFService } = await import('../services/pdfService');
  await PDFService.generatePDFFromAPI(apiCall, {
    ...options,
    loadingMessage: '正在生成PDF...',
    successMessage: options.downloadDirectly ? 'PDF下載已開始' : 'PDF已在新窗口中打開',
    errorMessage: 'PDF生成失敗，請稍後重試'
  });
};*/
